/*
 * wc-atc-guard.js — standalone parent-PDP Add-to-Cart guard + upload-zone wiggle.
 *
 * WHY THIS FILE EXISTS
 * ====================
 * On a customizable parent PDP the un-personalized product isn't sellable as-is
 * — the customer must upload a pet photo and generate art first. The "Phase 11.1"
 * guard intercepts a click on the page's own Add-to-Cart button (when no
 * generation exists yet) and shakes the upload zone instead of adding to cart.
 *
 * That guard used to live inside assets/wolden-customize.js (the legacy
 * monolith). But theme.liquid now loads EITHER the V2 module chain
 * (wcNN-init.js, when settings.enable_customize_v2 is on) OR the legacy
 * wolden-customize.js — never both. The V2 chain never ported the guard, so on
 * the live (V2) site clicking ATC on a parent PDP silently added the
 * un-customized parent product to the cart (confirmed 2026-05-26).
 *
 * This file is a self-contained, dependency-free re-implementation loaded
 * UNCONDITIONALLY by theme.liquid via {{ 'wc-atc-guard.js' | asset_url }} — so
 * it works under both V1 and V2, and (because it's referenced through
 * asset_url, not a bare ES-module import) Shopify auto-versions it, sidestepping
 * the wcNN chain's CDN edge-cache lock. It reads only shared globals
 * (window.WC._currentResult) and the DOM, so it needs nothing from either chain.
 *
 * Idempotent: bails if a guard is already wired (legacy sets WC.__atcGuardWired;
 * we set both that and window.__wcAtcGuardWired) so V1 + this never double-fire.
 */
(function () {
  'use strict';

  function alreadyWired() {
    if (window.__wcAtcGuardWired) return true;
    if (window.WC && window.WC.__atcGuardWired) return true;
    return false;
  }
  if (alreadyWired()) return;
  window.__wcAtcGuardWired = true;
  try { if (window.WC) window.WC.__atcGuardWired = true; } catch (e) {}

  // 2026-07-01 BUG FIX: on the live V2 chain nothing defined WC.track (only
  // the legacy monolith did), so this fell through to raw posthog.capture
  // WITHOUT the wolden_ prefix — atc_blocked_no_generation was invisible to
  // every wolden_* query. Now: (1) wc77-analytics.js exports WC.track (the
  // 3-leg fan-out: PostHog + direct GA4 + first-party beacon, with the
  // flow-test envelope), so the preferred path lands the event everywhere as
  // wolden_atc_blocked_no_generation; (2) the raw-PostHog fallback (chain not
  // booted yet / V1) prefixes wolden_ itself so the name is identical on
  // both paths. Historical unprefixed rows need an analysis alias only.
  function track(event) {
    try {
      if (window.WC && typeof window.WC.track === 'function') { window.WC.track(event); return; }
      if (window.posthog && typeof window.posthog.capture === 'function') { window.posthog.capture('wolden_' + event); return; }
    } catch (e) { /* analytics is best-effort */ }
  }

  // Shake the upload zone to draw the eye to "do this first".
  // 2026-07-01 (Flow B) — the watercolor arm's upload block is a different
  // element (.wolden-flow-b__upload); fall back to it when the classic zone
  // was removed at boot for a non-"current" flow arm.
  // 2026-07-01 (Flow C) — same again for the wizard arm's dropzone card
  // (.wolden-flow-c__dropzone): its module relabels the native ATC button
  // into the "CREATE YOUR PORTRAIT TO ADD TO CART" bar, and THIS wiggle is
  // what a pre-generation click on that bar does.
  function wiggleUploadZone() {
    var zone = document.querySelector('.wolden-customize__upload-zone') ||
               document.querySelector('.wolden-flow-b__upload') ||
               document.querySelector('.wolden-flow-c__dropzone');
    if (!zone) return;
    zone.classList.remove('wolden-customize__upload-zone--shake');
    // Force reflow so the animation restarts if it's mid-cycle.
    void zone.offsetWidth;
    zone.classList.add('wolden-customize__upload-zone--shake');
    try { zone.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    setTimeout(function () {
      zone.classList.remove('wolden-customize__upload-zone--shake');
    }, 700);
    track('atc_blocked_no_generation');
  }

  // Capture phase so we run before the theme's own button handlers and can
  // stopPropagation() before Horizon's add-to-cart logic fires.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(
      'add-to-cart-component button, ' +
      'buy-buttons-component button, ' +
      '.product-form__submit, ' +
      '[data-add-to-cart], ' +
      'button[name="add"]'
    );
    if (!trigger) return;

    // Our own reveal modal has its own ATC handler — never intercept that.
    if (trigger.closest('[data-wc-component="modal"]')) return;

    // No upload zone on this page => non-customizable product => normal ATC.
    // 2026-07-01 (Flow B) — flow-b is the watercolor arm's upload surface;
    // wc77-init removes the classic zone from the DOM for that arm, so the
    // guard must also recognize the flow-b component or watercolor visitors
    // could native-ATC the un-personalized parent product pre-generation.
    // 2026-07-01 (Flow C) — identical reasoning for the wizard arm (flow-c).
    if (!document.querySelector('[data-wc-component="upload-zone"], [data-wc-component="flow-b"], [data-wc-component="flow-c"]')) return;

    // Auto-generated CHILD products are real, purchasable, already-personalized
    // variants. theme.liquid tags their <body> with wc-auto-child. Never block.
    if (document.body && document.body.classList.contains('wc-auto-child')) return;

    // A generation has been made this session. The OLD assumption here was
    // "the ATC button's variant was swapped to the new child product, so the
    // click is valid" — proven false 2026-07-24: on a RELOADED page, Horizon's
    // own variant-picker init rewrites the buy form's input[name=id] back to
    // the PARENT variant AFTER our hydration stamped the child variant, and
    // nothing re-stamps unless the customer clicks a picker. Two real orders
    // (#1439, #1445) shipped carrying the parent product because of this.
    // The generation's presence proves nothing about the form — re-assert the
    // child variant AT CLICK TIME, and never let a parent-variant submit
    // through with generation properties attached.
    var WC = window.WC;
    if (WC && WC._currentResult && WC._currentResult.generationId) {
      var state = enforceChildVariant(e);
      if (state === 'ok') return;        // form carries the child variant now
      // 'healing': variant unresolved — click was blocked, self-heal running.
      return;
    }

    // Otherwise: this is an un-customized parent PDP. Block + wiggle.
    e.preventDefault();
    e.stopPropagation();
    wiggleUploadZone();
  }, true);

  // ── 2026-07-24 child-variant enforcement (reload race fix) ────────────────

  // The main buy form (same scoping idea as wc77-selectors' _buyButtonsFormScope,
  // re-implemented locally because this file is dependency-free by design).
  function mainBuyForm() {
    return document.querySelector(
      'product-form-component:not([data-section-id*="recommend"]):not([data-section-id*="related"]) form[action*="/cart/add"], ' +
      '[data-section-type="main-product"] form[action*="/cart/add"]'
    );
  }

  // Resolve the child variant id for the CURRENT picker state from R.
  // Mirrors wc77-selectors' getCurrentVariantId three-branch logic; prefers
  // the shared export when the module chain published it.
  function resolveChildVariantId() {
    try {
      if (window.WC && typeof window.WC.getCurrentVariantId === 'function') {
        return window.WC.getCurrentVariantId() || null;
      }
    } catch (e) { /* fall through to local resolution */ }
    var R = window.WC && window.WC._currentResult;
    if (!R) return null;
    if (R.activeFormat === 'canvas_framed') {
      var f = R.canvasFramedShopify && R.canvasFramedShopify[R.activeCanvasFramedSize];
      return (f && f.variantByColor && f.variantByColor[R.activeCanvasFramedColor]) || null;
    }
    if (R.activeFormat === 'paint_kit') {
      var key = R.activePaintKitSize + '_' + String(R.activePaintKitFrame || '').replace(/\s+/g, '');
      return (R.paintKitShopify && R.paintKitShopify.variants && R.paintKitShopify.variants[key]) || null;
    }
    var c = R.canvasShopify && R.canvasShopify[R.activeCanvasSize || '8x10'];
    return (c && c.shopifyVariantId) || null;
  }

  function stampVariant(vid) {
    var form = mainBuyForm();
    if (!form) return false;
    var input = form.querySelector('input[name="id"]');
    if (!input) return false;
    input.value = String(vid);
    var scope = form.closest('product-form-component') || form;
    scope.querySelectorAll('add-to-cart-component, [data-add-to-cart], buy-buttons-component').forEach(function (el) {
      if (el.hasAttribute('data-variant-id')) el.setAttribute('data-variant-id', String(vid));
    });
    return true;
  }

  // Click-time invariant: an ATC click on a page with a live generation must
  // submit the CHILD variant. Returns 'ok' (child stamped, let the click run)
  // or 'healing' (unresolvable right now — click blocked, fetch-gen self-heal
  // kicked off; the button is re-clicked automatically when the heal lands).
  var _healInFlight = false;
  function enforceChildVariant(e) {
    var vid = resolveChildVariantId();
    if (vid) {
      stampVariant(vid);
      return 'ok';
    }
    // No child variant resolvable (e.g. a stored snapshot from a recovery path
    // that carried handles but no variant ids). Do NOT let the parent submit.
    e.preventDefault();
    e.stopPropagation();
    track('atc_blocked_variant_unresolved');
    var trigger = e.target.closest('button');
    selfHeal(trigger);
    return 'healing';
  }

  // Re-fetch the generation record (it carries the full per-size variant ids
  // once phase 1 has landed — verified live on both affected generations),
  // merge it into R, then re-click the button the customer pressed.
  // Same endpoints wc2-session.js compiles in (this guard is dependency-free
  // by design, so it carries its own copy — both values already ship to every
  // browser inside that module).
  var GUARD_ORCH_V1 = 'https://pboljz7py4yztpystvet2qcryi0egncw.lambda-url.us-east-2.on.aws/';
  var GUARD_ORCH_V2 = 'https://lyzv32bofp5symutf7snoe2rxi0avlzj.lambda-url.us-east-2.on.aws/';
  var GUARD_ORCH_TOKEN = 'q2BzB8woX0dgbBXsr-0TtQorjWz8lXi81Cc64RYtXvM';
  function guardOrchestratorUrl() {
    try {
      var s = window.WC_SETTINGS || {};
      var qs = new URL(location.href).searchParams.get('wc_v2');
      if (qs === '0') return GUARD_ORCH_V1;
      if (qs === '1' || s.enable_orchestrator_v2) return GUARD_ORCH_V2;
    } catch (e) { /* fall through */ }
    return GUARD_ORCH_V2;
  }

  function selfHeal(trigger) {
    if (_healInFlight) return;
    _healInFlight = true;
    var R = window.WC && window.WC._currentResult;
    var url = guardOrchestratorUrl();
    if (!R || !R.generationId || !url) { _healInFlight = false; return; }
    var prevLabel = trigger ? trigger.textContent : null;
    if (trigger) { trigger.disabled = true; trigger.textContent = 'Setting up your art…'; }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: GUARD_ORCH_TOKEN,
        action: 'fetch-gen',
        generationIdPrefix: String(R.generationId),
      }),
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (body) {
          var cs = body.canvas && body.canvas.shopify;
          var fs = body.canvasFramed && body.canvasFramed.shopify;
          if (cs && Object.keys(cs).length) R.canvasShopify = cs;
          if (fs && Object.keys(fs).length) R.canvasFramedShopify = fs;
          if (body.paintKit) R.paintKitShopify = body.paintKit;
          repairStoredSnapshot(R);
        }
      })
      .catch(function () { /* heal is best-effort; the guard keeps blocking */ })
      .then(function () {
        _healInFlight = false;
        if (trigger) { trigger.disabled = false; if (prevLabel != null) trigger.textContent = prevLabel; }
        // If the heal produced a resolvable variant, complete the customer's
        // intent: re-click, which re-enters the guard and now passes 'ok'.
        if (resolveChildVariantId() && trigger) trigger.click();
        track(resolveChildVariantId() ? 'atc_variant_selfheal_ok' : 'atc_variant_selfheal_failed');
      });
  }

  // The gen-store's add() is insert-only per gen id, so a snapshot frozen
  // without variant ids is otherwise permanent (24h TTL) and every reload
  // would re-enter the heal. Patch the stored snapshot in place with the
  // healed maps so the NEXT reload hydrates complete data.
  function repairStoredSnapshot(R) {
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (!k || k.indexOf('wc.gens.') !== 0) continue;
        var data = JSON.parse(localStorage.getItem(k));
        if (!data || !data.gens) continue;
        var touched = false;
        for (var g = 0; g < data.gens.length; g++) {
          if (data.gens[g].id !== R.generationId || !data.gens[g].snapshot) continue;
          data.gens[g].snapshot.canvasShopify = R.canvasShopify || data.gens[g].snapshot.canvasShopify;
          data.gens[g].snapshot.canvasFramedShopify = R.canvasFramedShopify || data.gens[g].snapshot.canvasFramedShopify;
          if (R.paintKitShopify) data.gens[g].snapshot.paintKitShopify = R.paintKitShopify;
          touched = true;
        }
        if (touched) localStorage.setItem(k, JSON.stringify(data));
      }
    } catch (e) { /* best-effort — the click-time guard still protects */ }
  }

  // Passive repair: fix the form as soon as the page settles, so even the
  // display state (not just the click) points at the child product. Horizon's
  // init order isn't observable from here, so re-stamp on a short schedule —
  // stamping is idempotent and cheap. Only acts when a generation is live and
  // the form still holds a NON-child variant.
  function passiveRestamp() {
    var R = window.WC && window.WC._currentResult;
    if (!R || !R.generationId) return;
    var vid = resolveChildVariantId();
    if (!vid) return;
    var form = mainBuyForm();
    var input = form && form.querySelector('input[name="id"]');
    if (input && String(input.value) !== String(vid)) stampVariant(vid);
  }
  [1200, 3000, 6000].forEach(function (ms) { setTimeout(passiveRestamp, ms); });
})();
